import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * HowToJoin — Guide for new members to get online.
 * 
 * The Country Club uses a pre-configured emulator pack, so setup is simplified:
 *   1. Download the pack (comes with PCSX2 + BIOS + config)
 *   2. Get your game ISOs
 *   3. Find the right DNS for your game in the catalog
 *   4. Connect and play
 */

const STEPS = [
  {
    number: '01',
    title: 'Download the Emulator Pack',
    icon: '📦',
    color: 'rgba(59,130,246,0.8)',
    bg: 'rgba(59,130,246,0.06)',
    border: 'rgba(59,130,246,0.15)',
    content: (
      <>
        <p>The Country Club provides a <strong>pre-configured emulator pack</strong> that comes with everything you need — PCSX2, BIOS, network settings, and optimized configs. No manual setup required.</p>
        <div className="mt-4 space-y-2">
          <a href="https://cloud.socom.gg/s/ZnXtxY3DM8JfxGY" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-mono hover:bg-blue-500/20 transition-colors">
            <span>↗</span> Download Emulator Pack
          </a>
        </div>
        <ul className="mt-4 space-y-1.5 text-white/50 text-sm">
          <li>• Extract the zip to any folder on your PC</li>
          <li>• Run <code className="text-emerald-400/80 bg-white/[0.04] px-1.5 py-0.5 rounded text-xs">pcsx2-qt.exe</code> from the extracted folder</li>
          <li>• Windows, Mac, and Linux supported</li>
          <li>• Everything is pre-configured — no settings to change</li>
        </ul>
        <div className="mt-3 p-3 rounded-lg bg-amber-500/[0.04] border border-amber-500/15">
          <p className="text-xs font-mono text-amber-400/70">
            💡 <strong>Already have PCSX2?</strong> You can use your own setup — just make sure networking is enabled in Settings → Network & HDD.
          </p>
        </div>
      </>
    ),
  },
  {
    number: '02',
    title: 'Get Your Game ISOs',
    icon: '💿',
    color: 'rgba(168,85,247,0.8)',
    bg: 'rgba(168,85,247,0.06)',
    border: 'rgba(168,85,247,0.15)',
    content: (
      <>
        <p>You'll need <strong>ISO files</strong> of the PS2 games you want to play online. These should be ripped from discs you own.</p>
        <ul className="mt-4 space-y-1.5 text-white/50 text-sm">
          <li>• Use <code className="text-emerald-400/80 bg-white/[0.04] px-1.5 py-0.5 rounded text-xs">ImgBurn</code> or similar to rip from your PS2 discs</li>
          <li>• ISO format is preferred</li>
          <li>• Check the region tag in the <Link to="/" className="text-emerald-400/80 hover:text-emerald-400 transition-colors underline underline-offset-2">game catalog</Link> — some games need specific versions (NTSC vs PAL)</li>
        </ul>
      </>
    ),
  },
  {
    number: '03',
    title: 'Find Your Game\'s DNS',
    icon: '🌐',
    color: 'rgba(236,72,153,0.8)',
    bg: 'rgba(236,72,153,0.06)',
    border: 'rgba(236,72,153,0.15)',
    content: ({ popularDns }) => (
      <>
        <p>Each game connects to its own <strong>revival server</strong> via a custom DNS address. Different games use different servers — check which one supports the game you want to play.</p>

        <div className="mt-4 p-4 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2 text-sm text-white/60">
          <p><span className="text-white/80 font-semibold">1.</span> Browse the <Link to="/" className="text-emerald-400/80 hover:text-emerald-400 transition-colors underline underline-offset-2">game catalog</Link> and find the game you want to play</p>
          <p><span className="text-white/80 font-semibold">2.</span> Click the <span className="text-emerald-400/80">DNS address</span> next to it to copy it</p>
          <p><span className="text-white/80 font-semibold">3.</span> In your game's Network Setup menu, set connection to <strong className="text-white/80">Manual</strong></p>
          <p><span className="text-white/80 font-semibold">4.</span> Enter the copied DNS address — leave everything else on auto/default</p>
        </div>

        {/* Popular DNS quick reference */}
        {popularDns.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider mb-2">Popular DNS Servers</p>
            <div className="space-y-1.5">
              {popularDns.map(({ dns, count, games }) => (
                <div key={dns} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                  <code className="text-xs font-mono text-emerald-400/80 flex-shrink-0">{dns}</code>
                  <span className="text-[10px] font-mono text-white/25">·</span>
                  <span className="text-[10px] text-white/35 truncate">{count} games — {games}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3 p-3 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/15">
          <p className="text-xs font-mono text-emerald-400/70">
            💡 <strong>Different games, different servers.</strong> Always check the catalog for the correct DNS — not all games use the same address.
          </p>
        </div>
      </>
    ),
  },
  {
    number: '04',
    title: 'Connect & Play!',
    icon: '🎮',
    color: 'rgba(34,197,94,0.9)',
    bg: 'rgba(34,197,94,0.08)',
    border: 'rgba(34,197,94,0.2)',
    content: (
      <>
        <p>Once DNS is configured in-game, select <strong>"Online"</strong> or <strong>"Network Play"</strong> from the game's main menu and you'll connect to the revival servers.</p>
        <ul className="mt-4 space-y-1.5 text-white/50 text-sm">
          <li>• Check the <Link to="/" className="text-emerald-400/80 hover:text-emerald-400 transition-colors underline underline-offset-2">Clubhouse</Link> to see who's currently online</li>
          <li>• Browse <Link to="/events" className="text-emerald-400/80 hover:text-emerald-400 transition-colors underline underline-offset-2">Community Events</Link> for scheduled game nights</li>
          <li>• Join our Discord to find teammates, get help, and coordinate games</li>
        </ul>
        <a
          href="https://discord.gg/DQhhgYFH8"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-mono transition-all hover:scale-[1.02]"
          style={{
            background: 'linear-gradient(135deg, rgba(88,101,242,0.15), rgba(88,101,242,0.06))',
            border: '1px solid rgba(88,101,242,0.25)',
            color: 'rgba(88,101,242,0.9)',
          }}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.492c-1.53-.742-3.17-1.297-4.885-1.515a.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.566 18.566 0 00-5.487 0 12.36 12.36 0 00-.617-1.25.077.077 0 00-.079-.037c-1.714.218-3.354.773-4.885 1.515a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z" /></svg>
          Join Discord Server
        </a>
      </>
    ),
  },
];

export default function HowToJoin() {
  const [expandedStep, setExpandedStep] = useState(0);
  const [popularDns, setPopularDns] = useState([]);

  // Fetch catalog to build popular DNS reference
  useEffect(() => {
    fetch('/api/catalog')
      .then(r => r.json())
      .then(catalog => {
        if (!Array.isArray(catalog)) return;
        // Group games by DNS and count
        const dnsMap = {};
        catalog.forEach(g => {
          const raw = (g.dns || '').trim();
          if (!raw || raw.toUpperCase() === 'N/A') return;
          const dns = raw.split(' or ')[0].split('/')[0].trim();
          if (!dns || dns.toUpperCase() === 'N/A') return;
          if (!dnsMap[dns]) dnsMap[dns] = [];
          dnsMap[dns].push(g.name);
        });
        // Sort by game count, take top 4
        const sorted = Object.entries(dnsMap)
          .sort((a, b) => b[1].length - a[1].length)
          .slice(0, 4)
          .map(([dns, games]) => ({
            dns,
            count: games.length,
            games: games.slice(0, 3).join(', ') + (games.length > 3 ? '...' : ''),
          }));
        setPopularDns(sorted);
      })
      .catch(() => {});
  }, []);

  return (
    <section className="px-4 md:px-6 py-12 md:py-20">
      <div className="max-w-3xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="flex items-center gap-4 mb-4 justify-center">
            <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-transparent via-white/[0.1] to-transparent" />
            <span className="text-[10px] font-mono text-white/30 tracking-[0.3em] uppercase">Getting Started</span>
            <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-transparent via-white/[0.1] to-transparent" />
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-4">
            How to <span className="text-emerald-400">Join</span>
          </h1>
          <p className="text-sm md:text-base text-white/45 max-w-lg mx-auto leading-relaxed">
            Get from zero to online in 4 steps. Our pre-configured emulator pack handles the hard stuff — you just pick a game and play.
          </p>
        </div>

        {/* Quick summary */}
        <div className="mb-10 p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
          <h3 className="text-xs font-mono text-emerald-400/70 uppercase tracking-wider mb-3 flex items-center gap-2">
            <span>⚡</span> The Short Version
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            {[
              { icon: '📦', label: 'Download pack', detail: 'Pre-configured PCSX2' },
              { icon: '💿', label: 'Add your ISOs', detail: 'PS2 game files' },
              { icon: '🌐', label: 'Set DNS', detail: 'From our catalog' },
              { icon: '🎮', label: 'Play!', detail: 'That\'s it.' },
            ].map((req) => (
              <div key={req.label} className="flex items-center sm:flex-col sm:items-start gap-3 sm:gap-1.5 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <span className="text-lg leading-none">{req.icon}</span>
                <div>
                  <p className="text-sm text-white/70 font-medium">{req.label}</p>
                  <p className="text-[11px] text-white/30 font-mono">{req.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {STEPS.map((step, i) => {
            const isExpanded = expandedStep === i;
            return (
              <div
                key={step.number}
                className="rounded-xl border transition-all duration-300"
                style={{
                  background: isExpanded ? step.bg : 'rgba(255,255,255,0.015)',
                  borderColor: isExpanded ? step.border : 'rgba(255,255,255,0.05)',
                }}
              >
                <button
                  onClick={() => setExpandedStep(isExpanded ? -1 : i)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left cursor-pointer"
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-lg transition-transform duration-300"
                    style={{
                      background: step.bg,
                      border: `1px solid ${step.border}`,
                      transform: isExpanded ? 'scale(1.1)' : 'scale(1)',
                    }}
                  >
                    {step.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[10px] font-mono tracking-wider" style={{ color: step.color }}>{step.number}</span>
                      <h3 className={`text-sm md:text-base font-bold transition-colors ${isExpanded ? 'text-white' : 'text-white/70'}`}>
                        {step.title}
                      </h3>
                    </div>
                  </div>

                  <svg
                    className={`w-4 h-4 text-white/30 transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{
                    maxHeight: isExpanded ? '800px' : '0',
                    opacity: isExpanded ? 1 : 0,
                  }}
                >
                  <div className="px-5 pb-5 pl-[4.75rem] text-sm text-white/60 leading-relaxed">
                    {typeof step.content === 'function'
                      ? step.content({ popularDns })
                      : step.content
                    }
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col items-center gap-4 p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <span className="text-3xl">🏌️</span>
            <p className="text-sm text-white/50 max-w-sm">
              Still stuck? Drop by the Discord — the community is always happy to help new members get online.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href="https://discord.gg/DQhhgYFH8"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-lg text-sm font-mono font-semibold transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, rgba(88,101,242,0.15), rgba(88,101,242,0.06))',
                  border: '1px solid rgba(88,101,242,0.25)',
                  color: 'rgba(88,101,242,0.9)',
                }}
              >
                💬 Ask on Discord
              </a>
              <Link
                to="/"
                className="px-5 py-2.5 rounded-lg text-sm font-mono font-semibold text-emerald-400/80 transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(34,197,94,0.04))',
                  border: '1px solid rgba(34,197,94,0.2)',
                }}
              >
                ⛳ Browse the Catalog
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
